import CloseIcon from '@mui/icons-material/Close'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import { Button, IconButton, Snackbar, Typography } from '@mui/material'
import * as React from 'react'
import { useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { pickUnseenEntry, readLastSeen, writeLastSeen } from './releaseNotes'
import { useReleaseNotes } from './useReleaseNotes'

/**
 * "What's new" — a NON-BLOCKING corner notice, shown once per browser for
 * the latest release note after the vet lands in the authenticated layout.
 * It names the note and offers "What's new" (opens the full text under
 * Help → What's new) or dismiss; either counts as acknowledged.
 *
 * Deliberately a Snackbar, not a Dialog. The first cut was a modal: it
 * rendered perfectly on beta and its backdrop intercepted every click on the
 * page behind it — the whole live smoke suite failed on "subtree intercepts
 * pointer events" (2026-09-11), and a vet mid-chart-review would have been
 * interrupted the same way. Same rule as UpdateAvailableBanner: nudge, never
 * block. The full note lives on the page, so the notice can stay small.
 *
 * Mounted next to UpdateAvailableBanner in the Layout. After a vet refreshes
 * into a new bundle, that bundle's manifest carries the new note, so the two
 * features chain without talking to each other.
 */
export const WhatsNewNotice: React.FC = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const { entries, loading } = useReleaseNotes()
  const [acknowledged, setAcknowledged] = React.useState(false)

  const entry = React.useMemo(
    () => (loading || acknowledged ? null : pickUnseenEntry(entries, readLastSeen(), Date.now())),
    [entries, loading, acknowledged],
  )

  const acknowledge = () => {
    if (entry) writeLastSeen(entry.id)
    setAcknowledged(true)
  }

  const open = () => {
    acknowledge()
    navigate('/whats-new')
  }

  return (
    <Snackbar
      open={entry !== null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      // No auto-hide, no click-away, no Escape: one heads-up per browser, but
      // it is the vet who decides when it goes — not a timer, a stray click,
      // or an Escape aimed at something else. MUI's Snackbar listens for
      // Escape on the DOCUMENT, so without this a vet closing a menu or
      // pressing Escape on a plain field would mark the note seen for good
      // (eng-brain P66-7). Only the two buttons acknowledge.
      onClose={(_e, reason) => {
        if (reason !== 'clickaway' && reason !== 'escapeKeyDown') acknowledge()
      }}
      data-testid="whats-new-notice"
      // BELOW the modal layer. MUI stacks snackbars (1400) above dialogs
      // (1300), so at the default z-index this box floated over the bottom-
      // right corner of any open dialog and covered its Save button — the
      // live smoke suite caught it on the My Profile dialog (beta 5.1.91).
      // A dialog and its backdrop must always win over a heads-up.
      sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
      message={
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: 380 }}>
          <NewReleasesIcon fontSize="small" sx={{ mt: '2px' }} />
          <span>
            <Typography variant="subtitle2" component="span" display="block">
              {translate('pos.whats_new.dialog_title')}
            </Typography>
            <Typography variant="body2" component="span" display="block">
              {entry?.title}
            </Typography>
          </span>
        </span>
      }
      action={
        <>
          <Button color="inherit" size="small" onClick={open} sx={{ fontWeight: 'bold' }}>
            {translate('pos.whats_new.title')}
          </Button>
          <IconButton
            size="small"
            color="inherit"
            onClick={acknowledge}
            aria-label={translate('pos.whats_new.dismiss')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
      ContentProps={{
        sx: {
          backgroundColor: '#e8f5e9',
          color: '#1b5e20',
          border: '1px solid #c8e6c9',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          alignItems: 'flex-start',
        },
      }}
    />
  )
}

export default WhatsNewNotice
